import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Paperclip, Mic, X, Image, Video, StopCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatBox({ ride, socket, currentUserRole, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);

  const otherParticipant = currentUserRole === 'rider' 
    ? { 
        name: ride.driver_name || 'Driver', 
        username: ride.driver_username || '',
        role: 'driver' 
      }
    : { 
        name: ride.rider_name || 'Rider', 
        username: ride.rider_username || '',
        role: 'rider' 
      };

  useEffect(() => {
    fetchMessages();

    // Listen for new messages
    if (socket) {
      socket.on('new_message', (message) => {
        setMessages(prev => [...prev, message]);
        scrollToBottom();
      });
    }

    return () => {
      if (socket) {
        socket.off('new_message');
      }
    };
  }, [socket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/rides/${ride.id}/messages`);
      setMessages(response.data);
      scrollToBottom();
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    try {
      let messageData = {
        message_type: 'text',
        content: newMessage.trim() || 'Sent an attachment',
        file_data: null,
      };

      if (selectedFile) {
        const reader = new FileReader();
        const fileDataPromise = new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(selectedFile);
        });

        const fileData = await fileDataPromise;
        
        if (selectedFile.type.startsWith('image/')) {
          messageData.message_type = 'image';
        } else if (selectedFile.type.startsWith('video/')) {
          messageData.message_type = 'video';
        }
        
        messageData.file_data = fileData;
      }

      const response = await axios.post(`${API}/rides/${ride.id}/messages`, messageData);
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      setSelectedFile(null);
      setFilePreview(null);
      scrollToBottom();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        
        reader.onloadend = async () => {
          try {
            await axios.post(`${API}/rides/${ride.id}/messages`, {
              message_type: 'audio',
              content: 'Voice message',
              file_data: reader.result,
            });
            fetchMessages();
          } catch (error) {
            toast.error('Failed to send voice message');
          }
        };
        
        reader.readAsDataURL(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success('Recording started');
    } catch (error) {
      toast.error('Failed to access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Recording stopped');
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed bottom-0 right-0 w-full md:w-96 h-[500px] bg-dark-surface border-l border-t border-white/10 z-50 flex flex-col shadow-2xl">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-dark-bg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-neon-cyan flex items-center justify-center">
            <span className="text-black font-bold text-sm">
              {otherParticipant.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
          <div>
            <div className="font-medium text-white">{otherParticipant.name}</div>
            <div className="text-xs text-white/60">
              {otherParticipant.username ? `@${otherParticipant.username}` : ''} • <span className="capitalize">{otherParticipant.role}</span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          data-testid="close-chat-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-white/40 py-10">
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation!</p>
          </div>
        )}
        
        {messages.map((msg) => {
          const isOwn = msg.sender_role === currentUserRole;
          return (
            <div
              key={msg.id}
              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${msg.id}`}
            >
              <div className={`max-w-[80%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                {msg.message_type === 'text' && (
                  <div
                    className={`px-4 py-2 rounded-2xl ${
                      isOwn
                        ? 'bg-neon-cyan text-black'
                        : 'bg-dark-bg text-white'
                    }`}
                  >
                    <p className="text-sm break-words">{msg.content}</p>
                  </div>
                )}
                
                {msg.message_type === 'image' && msg.file_url && (
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`}
                      alt="Shared"
                      className="max-w-full h-auto max-h-64 object-cover"
                    />
                    {msg.content !== 'Sent an attachment' && (
                      <div className={`px-4 py-2 ${isOwn ? 'bg-neon-cyan text-black' : 'bg-dark-bg text-white'}`}>
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {msg.message_type === 'video' && msg.file_url && (
                  <div className="rounded-2xl overflow-hidden">
                    <video
                      src={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`}
                      controls
                      className="max-w-full h-auto max-h-64"
                    />
                  </div>
                )}
                
                {msg.message_type === 'audio' && msg.file_url && (
                  <div className={`px-4 py-2 rounded-2xl ${isOwn ? 'bg-neon-cyan' : 'bg-dark-bg'}`}>
                    <audio
                      src={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`}
                      controls
                      className="w-full"
                    />
                  </div>
                )}
                
                <span className="text-xs text-white/40 mt-1 px-2">
                  {formatTime(msg.created_at)}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* File Preview */}
      {filePreview && (
        <div className="px-4 py-2 border-t border-white/10 bg-dark-bg">
          <div className="relative inline-block">
            {selectedFile?.type.startsWith('image/') ? (
              <img src={filePreview} alt="Preview" className="h-20 rounded-lg" />
            ) : (
              <video src={filePreview} className="h-20 rounded-lg" />
            )}
            <button
              onClick={() => {
                setSelectedFile(null);
                setFilePreview(null);
              }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-white/10 bg-dark-bg">
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*,video/*"
            className="hidden"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/60 hover:text-white"
            data-testid="attach-file-btn"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type a message..."
            className="flex-1 bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl px-4 py-2 text-white placeholder:text-white/40"
            data-testid="message-input"
          />
          
          {isRecording ? (
            <button
              onClick={stopRecording}
              className="p-2 bg-red-500 rounded-lg transition-colors animate-pulse"
              data-testid="stop-recording-btn"
            >
              <StopCircle className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={startRecording}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/60 hover:text-white"
              data-testid="start-recording-btn"
            >
              <Mic className="w-5 h-5" />
            </button>
          )}
          
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() && !selectedFile}
            className="p-2 bg-neon-cyan text-black rounded-lg hover:bg-[#00B8D4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="send-message-btn"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}